import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { isActivateAccountFunnelEnabled } from '@/config/branding';
import { usePaymentStatus } from '@/hooks/use-payment-status';
import { usePrivilegedStaffBypass } from '@/hooks/use-privileged-staff-bypass';
import { ORG_ROUTES } from '@/config/router';

/**
 * Hard gate: CRM routes are unreachable until the account has a valid
 * default billing payment method on file (Stripe SetupIntent $0 flow).
 *
 * Bypass when:
 * - platform staff (AM / superadmin / founder), unless "View as User" is on
 * - whitelabel or partner-mode deploy (activation funnel is Tagada-only)
 */
export function PaymentMethodValidGuard() {
  const { hasPaymentMethod, isLoading: isLoadingPayment, isError } = usePaymentStatus();
  const { canBypass, isLoaded: claimsLoaded } = usePrivilegedStaffBypass();
  const location = useLocation();
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const funnelEnabled = isActivateAccountFunnelEnabled();

  if (!claimsLoaded) {
    return (
      <div className="flex h-full min-h-[50vh] items-center justify-center">
        <Loader2 className="text-muted-foreground h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (canBypass || !funnelEnabled) {
    return <Outlet />;
  }

  if (isLoadingPayment) {
    return (
      <div className="flex h-full min-h-[50vh] items-center justify-center">
        <Loader2 className="text-muted-foreground h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!hasPaymentMethod && !isError) {
    const activatePath = orgSlug
      ? ORG_ROUTES(orgSlug).ACTIVATE_ACCOUNT()
      : '/setup-payment';
    return <Navigate to={activatePath} state={{ from: location }} replace />;
  }

  return <Outlet />;
}

// Legacy export for backward compatibility
export const PaymentGuard = PaymentMethodValidGuard;
